import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../firebaseConfig";
import { statusLabels } from "../../../utils/officeScheme/officeSchemeStatus";
import { calculateOfficeSchemeFee, formatINR } from "../../../utils/officeScheme/gstCalculator";

const FLOW = [
  "APPLIED",
  "PAYMENT_DONE",
  "RNB_PENDING",
  "RNB_APPROVED",
  "ENGINEER_PENDING",
  "ENGINEER_APPROVED",
  "CA_PENDING",
  "CA_APPROVED",
  "REGISTRAR_PENDING",
  "REGISTRAR_APPROVED",
  "SUBSIDY_RELEASED",
];

export default function OfficeWorkflowMonitor() {
  const [apps, setApps] = useState([]);
  const [filter, setFilter] = useState("ALL");

  const fetchApps = async () => {
    const snap = await getDocs(collection(db, "office_scheme_applications"));
    setApps(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => {
    fetchApps();
  }, []);

  const filteredApps = useMemo(() => {
    if (filter === "ALL") return apps;
    if (filter === "PENDING_PAYMENT") return apps.filter((a) => a.paymentStatus !== "PAID");
    return apps.filter((a) => a.status === filter);
  }, [apps, filter]);

  const summary = useMemo(() => {
    return apps.reduce(
      (acc, app) => {
        const fee = calculateOfficeSchemeFee(app.estimateAmount);
        acc.totalApps += 1;
        acc.estimate += fee.totalEstimate;
        acc.invoice += fee.grandTotal;
        if (app.paymentStatus === "PAID") acc.paid += fee.grandTotal;
        else acc.pending += fee.grandTotal;
        return acc;
      },
      { totalApps: 0, estimate: 0, invoice: 0, paid: 0, pending: 0 }
    );
  }, [apps]);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow p-6 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">AI Workflow Monitor</h2>
          <p className="text-sm text-slate-500">
            Auto tracking, pending alerts, payment status and stage-wise monitoring.
          </p>
        </div>

        <button
          onClick={fetchApps}
          className="bg-blue-600 text-white px-4 py-2 rounded-xl"
        >
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card title="Applications" value={summary.totalApps} />
        <Card title="Estimate Value" value={formatINR(summary.estimate)} />
        <Card title="Invoice Value" value={formatINR(summary.invoice)} />
        <Card title="Paid" value={formatINR(summary.paid)} />
        <Card title="Outstanding" value={formatINR(summary.pending)} danger />
      </div>

      <div className="bg-white rounded-2xl shadow p-5">
        <div className="flex flex-wrap gap-2">
          <FilterBtn label="All" value="ALL" filter={filter} setFilter={setFilter} />
          <FilterBtn label="Payment Pending" value="PENDING_PAYMENT" filter={filter} setFilter={setFilter} />
          <FilterBtn label="R&B Pending" value="RNB_PENDING" filter={filter} setFilter={setFilter} />
          <FilterBtn label="Engineer Pending" value="ENGINEER_PENDING" filter={filter} setFilter={setFilter} />
          <FilterBtn label="CA Pending" value="CA_PENDING" filter={filter} setFilter={setFilter} />
          <FilterBtn label="Registrar Pending" value="REGISTRAR_PENDING" filter={filter} setFilter={setFilter} />
          <FilterBtn label="Subsidy Released" value="SUBSIDY_RELEASED" filter={filter} setFilter={setFilter} />
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow p-6 overflow-x-auto">
        <table className="w-full text-sm border">
          <thead className="bg-slate-100">
            <tr>
              <th className="border p-2">Society</th>
              <th className="border p-2">District</th>
              <th className="border p-2">Design</th>
              <th className="border p-2">Invoice</th>
              <th className="border p-2">Payment</th>
              <th className="border p-2">Current Stage</th>
              <th className="border p-2">AI Alert</th>
              <th className="border p-2">Progress</th>
            </tr>
          </thead>

          <tbody>
            {filteredApps.map((app) => {
              const fee = calculateOfficeSchemeFee(app.estimateAmount);
              const alert = getAiAlert(app);
              const progress = getProgress(app.status);

              return (
                <tr key={app.id}>
                  <td className="border p-2 font-bold">{app.societyName || "-"}</td>
                  <td className="border p-2">{app.district || "-"}</td>
                  <td className="border p-2">{app.selectedDesign?.sizeLabel || "-"}</td>
                  <td className="border p-2">{formatINR(fee.grandTotal)}</td>
                  <td className="border p-2">
                    <Badge done={app.paymentStatus === "PAID"}>
                      {app.paymentStatus || "PENDING"}
                    </Badge>
                  </td>
                  <td className="border p-2">{statusLabels[app.status] || app.status || "-"}</td>
                  <td className="border p-2">
                    <span className={alert.type === "danger" ? "text-red-600 font-bold" : "text-emerald-700 font-bold"}>
                      {alert.text}
                    </span>
                  </td>
                  <td className="border p-2 min-w-[180px]">
                    <div className="w-full bg-slate-200 rounded-full h-3">
                      <div
                        className="bg-emerald-600 h-3 rounded-full"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <p className="text-xs mt-1">{progress}% completed</p>
                  </td>
                </tr>
              );
            })}

            {filteredApps.length === 0 && (
              <tr>
                <td colSpan="8" className="border p-5 text-center text-slate-500">
                  No records found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function getProgress(status) {
  const index = FLOW.indexOf(status);
  if (index < 0) return 5;
  return Math.round(((index + 1) / FLOW.length) * 100);
}

function getAiAlert(app) {
  if (app.paymentStatus !== "PAID") {
    return { type: "danger", text: "Payment pending - follow up required" };
  }
  if (app.status === "RNB_PENDING") return { type: "danger", text: "R&B approval pending" };
  if (app.status === "ENGINEER_PENDING") return { type: "danger", text: "Engineer inspection pending" };
  if (app.status === "CA_PENDING") return { type: "danger", text: "CA utilization pending" };
  if (app.status === "REGISTRAR_PENDING") return { type: "danger", text: "Registrar approval pending" };
  if (app.status === "SUBSIDY_RELEASED") return { type: "ok", text: "Process completed" };
  return { type: "ok", text: "On track" };
}

function Card({ title, value, danger }) {
  return (
    <div className="bg-white rounded-2xl shadow p-4">
      <p className="text-xs text-slate-500">{title}</p>
      <h3 className={`text-lg font-bold ${danger ? "text-red-600" : "text-slate-800"}`}>
        {value}
      </h3>
    </div>
  );
}

function FilterBtn({ label, value, filter, setFilter }) {
  return (
    <button
      onClick={() => setFilter(value)}
      className={`px-4 py-2 rounded-xl font-semibold ${
        filter === value ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-700"
      }`}
    >
      {label}
    </button>
  );
}

function Badge({ done, children }) {
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-bold ${
      done ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
    }`}>
      {children}
    </span>
  );
}